/**
 * Font Size Context
 * Provides font scale adjustment for accessibility
 * Persists scale to localStorage and applies via CSS variable
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

const STORAGE_KEY = 'blia-events-font-scale';
const MIN_SCALE = 0.9;
const MAX_SCALE = 1.5;
const STEP = 0.1;
const DEFAULT_SCALE = 1.1;

interface FontSizeContextType {
  fontScale: number;
  setFontScale: (scale: number) => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
  canIncrease: boolean;
  canDecrease: boolean;
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

function getInitialScale(): number {
  if (typeof window === 'undefined') return DEFAULT_SCALE;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = parseFloat(stored);
    if (!isNaN(parsed) && parsed >= MIN_SCALE && parsed <= MAX_SCALE) {
      return parsed;
    }
  }
  return DEFAULT_SCALE;
}

function applyFontScale(scale: number) {
  document.documentElement.style.setProperty('--font-scale', scale.toString());
}

export function FontSizeProvider({ children }: { children: ReactNode }) {
  const [fontScale, setFontScaleState] = useState<number>(getInitialScale);

  // Apply scale on mount and when it changes
  useEffect(() => {
    applyFontScale(fontScale);
    localStorage.setItem(STORAGE_KEY, fontScale.toString());
  }, [fontScale]);

  const increaseFontSize = () => {
    setFontScaleState(current => {
      const newScale = Math.min(current + STEP, MAX_SCALE);
      return Math.round(newScale * 10) / 10; // Round to 1 decimal
    });
  };

  const decreaseFontSize = () => {
    setFontScaleState(current => {
      const newScale = Math.max(current - STEP, MIN_SCALE);
      return Math.round(newScale * 10) / 10;
    });
  };

  const resetFontSize = () => {
    setFontScaleState(DEFAULT_SCALE);
  };

  // Allow setting font scale directly (for settings page)
  const setFontScale = (scale: number) => {
    const clampedScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
    const roundedScale = Math.round(clampedScale * 10) / 10;
    setFontScaleState(roundedScale);
  };

  const canIncrease = fontScale < MAX_SCALE;
  const canDecrease = fontScale > MIN_SCALE;

  return (
    <FontSizeContext.Provider
      value={{
        fontScale,
        setFontScale,
        increaseFontSize,
        decreaseFontSize,
        resetFontSize,
        canIncrease,
        canDecrease,
      }}
    >
      {children}
    </FontSizeContext.Provider>
  );
}

export function useFontSize(): FontSizeContextType {
  const context = useContext(FontSizeContext);
  if (!context) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
}
